import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { SessionProvider } from "./lib/session";
import { App } from "./App";
import { installRipple } from "./lib/ripple";
import { applyBootModule } from "./lib/boot-module";
import "./index.css";

/**
 * Query defaults.
 *
 * `retry: 1` because a property manager on a patchy connection should get one silent retry, but
 * not four — a screen that hangs for twenty seconds is worse than one that says it failed.
 * Refetch on focus is on: this is a shared database, and coming back to a stale arrears figure
 * after a colleague recorded a payment is exactly the mistake the product exists to prevent.
 */
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      staleTime: 30_000,
      refetchOnWindowFocus: true,
    },
  },
});

/*
 * The module skin, before the first paint.
 *
 * Above `createRoot` deliberately: on a reload nothing else knows the module until the session
 * has come back, and until then the boot spinner was drawn in the letting green on a screen about
 * to be blue. `Layout` writes the authoritative value over this as soon as it has one — see
 * `lib/boot-module.ts` for why a guess is the right answer here and what it is guessed from.
 */
applyBootModule();

/** Press feedback on every button, from one delegated listener. See `lib/ripple.ts`. */
installRipple();

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <SessionProvider>
          <App />
        </SessionProvider>
      </BrowserRouter>
    </QueryClientProvider>
  </StrictMode>,
);
